/**
 * Loads the watching list and keeps it current.
 *
 * Refreshes on mount and whenever the app returns to the foreground — an
 * installed PWA can sit backgrounded for days, and a stale queue is the failure
 * people notice first.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { SimklClient } from "@/api/client";
import type { LibraryShow } from "@/api/schemas";
import { log } from "@/lib/log";
import { applyEdit, isSatisfied, supersede } from "@/lib/optimistic";
import type { OptimisticEdit } from "@/lib/optimistic";

export interface LibraryState {
  shows: readonly LibraryShow[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  /** Applies a write locally, before Simkl has confirmed it. */
  patch: (edit: OptimisticEdit) => void;
}

interface HeldEdit extends OptimisticEdit {
  appliedAt: number;
}

/**
 * Drops the edits Simkl has caught up with, and reports what is left.
 *
 * Outside any state updater deliberately: React invokes an updater twice in
 * development to catch impurity, which logged every fetch twice and made the
 * log lie about how many round trips had actually happened.
 */
/**
 * Adds an edit, after letting it overrule the episodes older edits claimed.
 *
 * Without that, a held edit the user has since contradicted is unsatisfiable
 * by definition, and re-applies over every fetch until it expires — mark a
 * season, correct it to episode three, and it reads as fully watched for
 * ninety seconds even though Simkl has it right.
 */
function withEdit(held: readonly HeldEdit[], edit: OptimisticEdit): HeldEdit[] {
  const kept = supersede(held, edit);
  if (kept.length !== held.length) {
    log("superseded a held edit", { show: edit.simklId, episodes: edit.targets.length });
  }
  return [...kept, { ...edit, appliedAt: Date.now() }];
}

function reconcile(held: readonly HeldEdit[], fetched: readonly LibraryShow[]): HeldEdit[] {
  const now = Date.now();
  const remaining = held.filter(
    (edit) => !isSatisfied(fetched, edit) && now - edit.appliedAt < HOLD_MS,
  );
  if (held.length > 0) {
    log("fetched", {
      shows: fetched.length,
      editsBefore: held.length,
      stillHeld: remaining.length,
    });
  }
  return remaining;
}

/**
 * How long an unconfirmed edit is held over fetched data. Long enough to
 * cover Simkl catching up, short enough that a write that genuinely failed
 * stops being papered over.
 */
const HOLD_MS = 90_000;

export function useLibrary(client: SimklClient, enabled: boolean): LibraryState {
  // What Simkl last said, kept separate from what the user has just done.
  const [fetched, setFetched] = useState<readonly LibraryShow[]>([]);
  const [edits, setEdits] = useState<readonly HeldEdit[]>([]);
  // A mirror of `edits` that the fetch callback can read without going stale,
  // and without making the reconciliation a state updater with a side effect.
  const heldEdits = useRef<readonly HeldEdit[]>([]);
  // Starts true when enabled so the first load needs no synchronous setState
  // from an effect, which would cascade an extra render.
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const live = useRef(true);

  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  /** Fetches without touching state first; every setState lands in a callback. */
  const fetchShows = useCallback(() => {
    // Both lists: Up Next needs "watching", the Library grid needs "completed"
    // too. Two requests, well inside the rate budget.
    Promise.all([client.library("watching"), client.library("completed")])
      .then(([watching, completed]) => {
        if (live.current) {
          const next = [...watching, ...completed];
          setFetched(next);
          // An edit stays on screen until Simkl agrees with it, or until it is
          // old enough that holding it would be hiding a failure rather than
          // covering a lag.
          setEdits(reconcile(heldEdits.current, next));
          setError(null);
        }
      })
      .catch((cause: unknown) => {
        if (live.current) {
          const message = cause instanceof Error ? cause.message : "Could not reach Simkl";
          log("fetch failed", message);
          setError(message);
        }
      })
      .finally(() => {
        if (live.current) setLoading(false);
      });
  }, [client]);

  /** For event handlers, where showing the spinner immediately is correct. */
  const refresh = useCallback(() => {
    setLoading(true);
    fetchShows();
  }, [fetchShows]);

  useEffect(() => {
    if (enabled) fetchShows();
  }, [enabled, fetchShows]);

  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchShows();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, fetchShows]);

  /**
   * The screen must move on the tap, not on the response. The refetch that
   * follows is the correction; Simkl's answer wins when it lands.
   */
  useEffect(() => {
    heldEdits.current = edits;
  }, [edits]);

  const patch = useCallback((edit: OptimisticEdit) => {
    log("applied locally", {
      show: edit.simklId,
      episodes: edit.targets.length,
      watched: edit.watched,
    });
    heldEdits.current = withEdit(heldEdits.current, edit);
    setEdits(heldEdits.current);
  }, []);

  // Held edits sit on top of the fetched data, so a refetch that has not yet
  // caught up cannot undo what the user just did.
  const shows = useMemo(
    () => edits.reduce<readonly LibraryShow[]>((acc, edit) => applyEdit(acc, edit), fetched),
    [fetched, edits],
  );

  return { shows, loading, error, refresh, patch };
}
